// Shared chrome for the WO timesheet tables (Day and Total views): grid
// template, header row, totals footer, and time display helpers, so both
// views stay visually identical.

// Extra trailing 32px column reserves space for the row edit button so the
// header/footer stay aligned with editable rows.
export const TIMESHEET_GRID =
  "grid items-center gap-4 px-6 grid-cols-[minmax(0,1.9fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.1fr)]";
export const TIMESHEET_GRID_WITH_ACTION =
  "grid items-center gap-4 px-6 grid-cols-[minmax(0,1.9fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.1fr)_32px]";

export const timesheetGridClass = (withAction) =>
  withAction ? TIMESHEET_GRID_WITH_ACTION : TIMESHEET_GRID;

// "H:MM" without hour padding: 150 -> "2:30", 19200 -> "320:00".
export const formatMinutes = (minutes) => {
  const safeMinutes = Math.max(0, Math.round(minutes || 0));
  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;
  return `${hours}:${String(remainingMinutes).padStart(2, "0")}`;
};

// Stored "HH:MM" entry -> display string; empty/zero collapses to an em dash.
export const displayStoredTime = (value) => {
  if (!value || typeof value !== "string") return "—";
  const trimmed = value.trim();
  if (!trimmed || trimmed === "00:00" || trimmed === "__:__") return "—";
  return trimmed;
};

// Body cell for a stored "HH:MM" value (regular / travel / overtime).
// Numeric columns are center-aligned so narrow values ("—") sit on the same
// vertical axis as their header label and the totals row.
export const StoredTimeCell = ({ value }) => {
  const display = displayStoredTime(value);
  return (
    <p
      className={`font-OpenSansRegular text-[14px] leading-[20px] text-center ${
        display === "—" ? "text-[#7C829A]" : "text-[#5F6477]"
      }`}
    >
      {display}
    </p>
  );
};

// Body cell for a computed per-row total.
export const TotalTimeCell = ({ minutes }) =>
  minutes > 0 ? (
    <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] text-center">
      {formatMinutes(minutes)}
    </p>
  ) : (
    <p className="font-OpenSansRegular text-[14px] leading-[20px] text-[#7C829A] text-center">—</p>
  );

const HEADER_LABEL_CLASS =
  "font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477]";
const FOOTER_VALUE_CLASS =
  "font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] text-center";

const displayFooterMinutes = (minutes) => (minutes > 0 ? formatMinutes(minutes) : "—");

export const TimesheetTableHeader = ({ withAction = false }) => (
  <div className={`${timesheetGridClass(withAction)} h-[44px] bg-[#FBFCFF] border-b border-[#E5E8F3]`}>
    <p className={HEADER_LABEL_CLASS}>Team Member</p>
    <p className={`${HEADER_LABEL_CLASS} text-center`}>Regular</p>
    <p className={`${HEADER_LABEL_CLASS} text-center`}>Travel</p>
    <p className={`${HEADER_LABEL_CLASS} text-center`}>Overtime</p>
    <p className={`${HEADER_LABEL_CLASS} text-center`}>Total</p>
    {withAction && <span />}
  </div>
);

export const TimesheetTableFooter = ({
  label,
  regularMinutes,
  travelMinutes,
  overMinutes,
  withAction = false,
}) => (
  <div className={`${timesheetGridClass(withAction)} h-[44px] bg-[#FBFCFF] border-t border-[#E5E8F3]`}>
    <p className={`${HEADER_LABEL_CLASS} uppercase`}>{label}</p>
    <p className={FOOTER_VALUE_CLASS}>{displayFooterMinutes(regularMinutes)}</p>
    <p className={FOOTER_VALUE_CLASS}>{displayFooterMinutes(travelMinutes)}</p>
    <p className={FOOTER_VALUE_CLASS}>{displayFooterMinutes(overMinutes)}</p>
    <p className={FOOTER_VALUE_CLASS}>
      {displayFooterMinutes(regularMinutes + travelMinutes + overMinutes)}
    </p>
    {withAction && <span />}
  </div>
);
